import {
  apiClient,
  mapDisplayStatusToBackend,
  DEFAULT_SORT_COLUMN,
  DEFAULT_SORT_DESC,
} from '@claim-os/shared';
import type { ClaimListResponse, ClaimListRow, ClaimRowSummary } from '@claim-os/shared';
import type { AssociatedListRow, Claim, ClaimsListResponse } from '../types';

/**
 * Service layer for the portal's claims list page.
 *
 * Reads encounter-service `GET /api/v1/claims` (one row per item or per-provider
 * claim). `mapClaimListRowToClaim` translates each wire row into the portal's
 * `Claim` display model at this seam.
 *
 * Page cache: page 1 is fetched on `getClaims()`, `getNextPage` fetches lazily and
 * appends to `pageHistory`, `getPreviousPage` serves from `pageHistory` without a
 * network call, and `invalidateClaimsCache()` resets it.
 */

function mapAssociatedRow(r: ClaimRowSummary): AssociatedListRow {
  return {
    rowKind: r.rowKind,
    number: r.number,
    claimType: r.billType,
    providerName: r.providerName ?? null,
    createdAt: r.createdAt,
  };
}

export function mapClaimListRowToClaim(row: ClaimListRow): Claim {
  const associatedRows = row.associatedRows ?? [];
  return {
    number: row.number,
    caseId: row.caseId,
    rowKind: row.rowKind,
    providerId: row.providerId ?? null,
    providerName: row.providerName ?? null,
    encounterId: row.encounterId,
    patientFirstName: row.patient.firstName,
    patientLastName: row.patient.lastName,
    patientDateOfBirth: row.patient.dateOfBirth,
    dateOfService: row.dateOfService ?? null,
    // billType is a code (OFFICE_VISIT, SURGICAL, …); the UI uses CLAIM_TYPE
    // to look up the display label when needed.
    claimType: row.billType,
    locationName: null,
    // Raw SharePoint status; StatusBadge maps it to the display vocabulary at render time.
    status: row.status,
    isGapCase: row.department === 'GAP',
    created: row.encounterCreatedAt,
    editableUntil: row.editableUntil ?? undefined,
    parentTicketId: null,
    associatedClaimsCount: associatedRows.length,
    associatedClaims: associatedRows.map(mapAssociatedRow),
    unresolvedEscalationThreadCount: row.unresolvedEscalationThreadCount,
    escalationAllResponded: row.escalationAllResponded,
    oldestUnresolvedEscalationAt: row.oldestUnresolvedEscalationAt ?? undefined,
    lastEscalationResponseAt: row.lastEscalationResponseAt ?? undefined,
    unresolvedEscalationSubjects: row.unresolvedEscalationSubjects,
  };
}

export interface ClaimsFilterParams {
  search?: string;
  /** Portal-side `type` filter → wire `billType`. */
  type?: string;
  /** Display status string; expanded to raw SharePoint status(es) on `?status=`. */
  status?: string;
  /** Provider id; sent as `providerId`. */
  providerId?: string;
  sortBy?: string;
  sortDesc?: boolean;
  /** Filter to only cases with ≥1 unresolved escalation thread. */
  escalated?: boolean;
  /** Finer escalation-signal filter — 'received' | 'awaiting'. */
  escalationStatus?: string;
}

function buildQueryString(
  pageSize: number,
  filters: ClaimsFilterParams,
  nextPageToken?: string,
): string {
  const qs = new URLSearchParams();
  qs.set('pageSize', String(pageSize));

  if (nextPageToken) {
    qs.set('nextPageToken', nextPageToken);
  }

  if (filters.search) qs.set('search', filters.search);
  // Portal `type` → wire `billType`; portal display `status` → raw backend
  // status(es) (e.g. 'Processing' → 'In Coding,SCA Coding Review,...').
  if (filters.type) qs.set('billType', filters.type);
  if (filters.status) qs.set('status', mapDisplayStatusToBackend(filters.status));
  if (filters.providerId) qs.set('providerId', filters.providerId);
  // The server's default order is the only one with the zero-skip paging guarantee,
  // so it is requested by omitting the sort params.
  const isDefaultSort =
    filters.sortBy === DEFAULT_SORT_COLUMN && filters.sortDesc === DEFAULT_SORT_DESC;
  if (!isDefaultSort) {
    if (filters.sortBy) qs.set('sortBy', filters.sortBy);
    if (filters.sortDesc !== undefined) qs.set('sortDesc', String(filters.sortDesc));
  }
  if (filters.escalated !== undefined) qs.set('escalated', String(filters.escalated));
  if (filters.escalationStatus) qs.set('escalationStatus', filters.escalationStatus);

  return qs.toString();
}

// ----------------------------------------------------------------------------
// Page cache
// ----------------------------------------------------------------------------

interface PageCache {
  data: Claim[];
  nextToken: string | null;
}

let pageHistory: PageCache[] = [];
let currentPageIndex = -1;
let currentFilters: ClaimsFilterParams = {};

/** Fetch first page of claims from the API. Resets the page cache. */
export async function getClaims(
  pageSize: number = 25,
  filters: ClaimsFilterParams = {},
): Promise<ClaimsListResponse> {
  pageHistory = [];
  currentPageIndex = -1;
  currentFilters = filters;

  const queryString = buildQueryString(pageSize, filters);

  const response = await apiClient.get<ClaimListResponse>(`/api/v1/claims?${queryString}`);
  const { rows, nextPageToken } = response.data;
  const claims = (rows ?? []).map(mapClaimListRowToClaim);

  pageHistory.push({ data: claims, nextToken: nextPageToken });
  currentPageIndex = 0;

  return {
    data: claims,
    pagination: {
      page: 1,
      pageSize,
      hasNextPage: nextPageToken != null,
      hasPrevPage: false,
    },
  };
}

/** Fetch next page of claims. Returns null when no next page exists. */
export async function getNextPage(
  pageSize: number = 25,
): Promise<ClaimsListResponse | null> {
  // Already cached?
  if (currentPageIndex < pageHistory.length - 1) {
    currentPageIndex++;
    const cached = pageHistory[currentPageIndex];
    return {
      data: cached.data,
      pagination: {
        page: currentPageIndex + 1,
        pageSize,
        hasNextPage:
          cached.nextToken != null || currentPageIndex < pageHistory.length - 1,
        hasPrevPage: currentPageIndex > 0,
      },
    };
  }

  // Need a server fetch.
  const currentPage = pageHistory[currentPageIndex];
  if (!currentPage?.nextToken) return null;

  const queryString = buildQueryString(pageSize, currentFilters, currentPage.nextToken);
  const response = await apiClient.get<ClaimListResponse>(`/api/v1/claims?${queryString}`);
  const { rows, nextPageToken } = response.data;
  const claims = (rows ?? []).map(mapClaimListRowToClaim);

  pageHistory.push({ data: claims, nextToken: nextPageToken });
  currentPageIndex++;

  return {
    data: claims,
    pagination: {
      page: currentPageIndex + 1,
      pageSize,
      hasNextPage: nextPageToken != null,
      hasPrevPage: currentPageIndex > 0,
    },
  };
}

/** Get previous page from the in-memory cache. Returns null when at page 1. */
export function getPreviousPage(pageSize: number = 25): ClaimsListResponse | null {
  if (currentPageIndex <= 0) return null;

  currentPageIndex--;
  const cached = pageHistory[currentPageIndex];

  return {
    data: cached.data,
    pagination: {
      page: currentPageIndex + 1,
      pageSize,
      hasNextPage:
        cached.nextToken != null || currentPageIndex < pageHistory.length - 1,
      hasPrevPage: currentPageIndex > 0,
    },
  };
}

export function hasNextPage(): boolean {
  if (currentPageIndex < pageHistory.length - 1) return true;
  const currentPage = pageHistory[currentPageIndex];
  return currentPage != null && currentPage.nextToken != null;
}

export function hasPreviousPage(): boolean {
  return currentPageIndex > 0;
}

export function getCurrentPageNumber(): number {
  return currentPageIndex + 1;
}

/**
 * Reset pagination state. Called after a successful submit or PATCH (so the
 * list re-fetches the freshly-changed first page) and implicitly when the
 * page invokes `getClaims` on a filter / sort change.
 */
export function invalidateClaimsCache(): void {
  pageHistory = [];
  currentPageIndex = -1;
  currentFilters = {};
}
